import { useCallback, useMemo, useState } from 'react';
import { catalogWidgetSpan } from '@aimsos/ui';

import {
  COLUMN_SPAN_BY_SIZE,
  PREVIEW_GRID_COLUMNS,
  PREVIEW_WIDGET_ID,
  sizeForColumnSpan,
} from '../constants/widgetPreviewLayout.js';

/** react-grid-layout wants a layout per breakpoint; the preview has one. */
const forEveryBreakpoint = (item) => ({
  lg: [item],
  md: [item],
  sm: [item],
  xs: [item],
  xxs: [item],
});

/**
 * The preview widget's position on the preview canvas (CT-5199).
 *
 * ## Why the arrangement is not simply state
 *
 * Two controls move the same widget: the `S M L` rail sets its width, and the
 * grid's own drag and resize handles set everything. If both wrote to one
 * layout object they would fight — the rail would clobber a drag, or a drag
 * would leave the rail claiming a size the widget no longer has.
 *
 * So `size` stays the parent's (it is form state, it is what gets saved) and
 * this hook owns only what the *grid* can tell you and the rail cannot:
 * position and height. `layouts` is derived from the two, with no effect
 * syncing them — an effect here would be a render-loop waiting to happen,
 * because the grid reports a layout change on mount and on every prop change.
 *
 * A width resize reports back through `onSizeChange`, so the rail always
 * describes the widget actually on screen.
 *
 * ## Why position is keyed by type
 *
 * Height comes from the type — a Table wants ten rows and a KPI wants five —
 * so switching type has to pick up the new default rather than keep the old
 * one. Keying the whole arrangement by type gets that for free, and has the
 * better side effect that going back to a type you already arranged returns
 * it where you left it.
 *
 * Nothing persists. A preview arrangement belongs to the widget being built,
 * and restoring the last one across sessions would silently place a new widget
 * where an unrelated old one sat.
 *
 * @param {object} args
 * @param {string} args.type Widget type id — picks the default height.
 * @param {'small'|'medium'|'large'} args.size The rail's current selection.
 * @param {(size: string) => void} args.onSizeChange Called when a resize changes the size.
 * @returns {{ layouts: object, onLayoutChange: Function }}
 */
export function useWidgetPreviewLayout({ type, size, onSizeChange }) {
  const [placementByType, setPlacementByType] = useState({});

  const span = catalogWidgetSpan(type);
  const placement = placementByType[type] ?? null;

  const layouts = useMemo(() => {
    const w = COLUMN_SPAN_BY_SIZE[size] ?? COLUMN_SPAN_BY_SIZE.medium;
    // Widening past the right edge would otherwise push the widget off the
    // grid and let the compactor drop it to a second row, which reads as the
    // preview losing the widget.
    const x = Math.min(placement?.x ?? 0, PREVIEW_GRID_COLUMNS - w);

    return forEveryBreakpoint({
      i: PREVIEW_WIDGET_ID,
      x,
      y: placement?.y ?? 0,
      w,
      h: placement?.h ?? span.h,
      minW: span.minW,
      maxW: PREVIEW_GRID_COLUMNS,
      minH: span.minH,
    });
  }, [placement, size, span]);

  const onLayoutChange = useCallback(
    (layout) => {
      const item = layout?.find((entry) => entry.i === PREVIEW_WIDGET_ID);
      if (!item) return;

      setPlacementByType((current) => {
        const previous = current[type];
        if (previous?.x === item.x && previous?.y === item.y && previous?.h === item.h) {
          // The grid reports the layout it was handed, on mount and after every
          // prop change. Bailing on an unchanged placement is what stops that
          // echo from becoming a render loop.
          return current;
        }
        return { ...current, [type]: { x: item.x, y: item.y, h: item.h } };
      });

      const nextSize = sizeForColumnSpan(item.w);
      if (nextSize !== size) onSizeChange(nextSize);
    },
    [onSizeChange, size, type],
  );

  return { layouts, onLayoutChange };
}

export default useWidgetPreviewLayout;
